import * as React from 'react'
import { FC, useState } from 'react'
import { useSelector } from 'react-redux'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import GestureIcon from '@mui/icons-material/Gesture'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import MergeIcon from '@mui/icons-material/CallMerge'
import Checkbox from '@mui/material/Checkbox'
import { selectTerritorySummaries } from '@user/store/territory'
import { Territory } from '@user/store/territory/typings'
import { DEFAULT_TERRITORY_COLOR } from '@user/store/territory/constants'
import { ownerCountLabel } from './territoryMetrics'

type Props = {
  onClose: () => void
  /** Fit the map to a territory's bounds, so the user can see what they are about to change. */
  onZoomTo: (territory: Territory) => void
  onEditDetails: (territory: Territory) => void
  onRedrawBoundary: (territory: Territory) => void
  /**
   * Asks the caller to confirm a delete. Handled there, not here, so the list can close and
   * the map can move to the territory first -- confirming a destructive action against a name
   * alone is how the wrong "Monmouth North"/"Monmouth South" gets removed.
   */
  onDelete: (territory: Territory) => void
  /**
   * Opens the performance report. Lives here rather than as another map control because this
   * panel is already the territory hub, and the control stack is crowded enough.
   */
  onViewPerformance: () => void
  /** Arms the lasso to draw the piece being carved off this territory. */
  onSplit: (territory: Territory) => void
  /** Hands the chosen territories to the merge dialog. */
  onMerge: (territories: Territory[]) => void
}

/**
 * Lists the tenant's territories so they can be found, edited and deleted (WWM-17508).
 *
 * A list rather than clicking a polygon on the map, deliberately. The territory layers carry no
 * click handler on purpose -- an always-on polygon beneath every pin must never compete for a
 * tap -- and adding one for management would undo that. A list also works for a territory that
 * is off screen, which clicking cannot.
 *
 * Admin-only: every action here hits a write endpoint gated on the Admin role.
 */
export const TerritoryManagePanel: FC<Props> = ({
  onClose,
  onZoomTo,
  onEditDetails,
  onRedrawBoundary,
  onDelete,
  onViewPerformance,
  onSplit,
  onMerge,
}) => {
  const territories = useSelector(selectTerritorySummaries)

  // Merge needs two or more, so the list switches into a selecting mode rather than offering a
  // per-row action that could only ever be half of an operation.
  const [merging, setMerging] = useState(false)
  const [selected, setSelected] = useState<number[]>([])

  const toggle = (id: number) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    )

  const leaveMergeMode = () => {
    setMerging(false)
    setSelected([])
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {merging ? 'Pick territories to merge' : 'Territories'}
      </DialogTitle>

      <DialogContent dividers>
        {territories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No territories yet. Territories are drawn from Settings › Lead
            Assignment Rules, on a rule&rsquo;s Territory condition — choose
            &ldquo;Draw a new territory&rdquo;.
          </Typography>
        ) : (
          <List disablePadding>
            {territories.map((territory) => (
              <ListItem
                key={territory.id}
                disablePadding
                secondaryAction={
                  merging ? undefined : (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit name, colour and owners">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => onEditDetails(territory)}
                          aria-label={`Edit ${territory.name}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Draw this territory's shape again">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => onRedrawBoundary(territory)}
                          aria-label={`Redraw ${territory.name}`}
                        >
                          <GestureIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Split off part of this territory">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => onSplit(territory)}
                          aria-label={`Split ${territory.name}`}
                        >
                          <ContentCutIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => onDelete(territory)}
                          aria-label={`Delete ${territory.name}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )
                }
              >
                {/* Clicking the row moves the map there -- the cheapest way to answer
                      "which one is this?" before editing or deleting it. */}
                <ListItemButton
                  onClick={() =>
                    merging ? toggle(territory.id) : onZoomTo(territory)
                  }
                  sx={{ pr: merging ? 2 : 18 }}
                >
                  {merging && (
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={selected.includes(territory.id)}
                      tabIndex={-1}
                      disableRipple
                      sx={{ mr: 0.5 }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '3px',
                      mr: 1.5,
                      flexShrink: 0,
                      bgcolor: territory.colorHex ?? DEFAULT_TERRITORY_COLOR,
                    }}
                  />
                  <ListItemText
                    primary={territory.name}
                    secondary={
                      // Blank rather than "0 owners" for an unowned territory -- see
                      // ownerCountLabel. A territory routed by a rule has no owners of its own,
                      // and "0 owners" reads as nobody covering it.
                      ownerCountLabel(territory.ownerIds.length)
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {merging ? (
          <>
            <Button onClick={leaveMergeMode}>Cancel</Button>
            <Button
              variant="contained"
              disabled={selected.length < 2}
              onClick={() =>
                onMerge(territories.filter((t) => selected.includes(t.id)))
              }
              data-test-id="territory-merge-start"
            >
              {/* Says WHY it is disabled rather than simply being grey: two is the minimum
                  that means anything, and a dead button explains nothing. */}
              {selected.length < 2
                ? 'Pick two or more'
                : `Merge ${selected.length}`}
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={onViewPerformance}
                data-test-id="territory-performance-open"
              >
                View performance
              </Button>
              <Button
                startIcon={<MergeIcon />}
                onClick={() => setMerging(true)}
                disabled={territories.length < 2}
                data-test-id="territory-merge-mode"
              >
                Merge
              </Button>
            </Box>
            <Button onClick={onClose}>Close</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
